import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { heroImage, services, banks, howItWorks, testimonials } from '@/mock/data';
import EMICalculator from './EMICalculator';
import SEO from '@/components/site/SEO';
import Reveal from '@/components/site/Reveal';
import CountUp from '@/components/site/CountUp';
import {
  Star,
  ShieldCheck,
  Clock3,
  BadgeCheck,
  Percent,
  IndianRupee,
  Users,
  Building2,
  Phone,
  ChevronRight,
  Plus,
  Minus,
  ArrowRight,
  CreditCard,
  TrendingUp,
  Scale,
  Search,
  ClipboardCheck,
  FileText,
  Banknote,
} from 'lucide-react';

const SERVICE_ICON_MAP = {
  loans: CreditCard,
  investments: TrendingUp,
  debt: Scale,
  'home-search': Search,
};

const HOW_ICON_MAP = {
  1: ClipboardCheck,
  2: FileText,
  3: BadgeCheck,
  4: Banknote,
};

/* ---------- helpers ---------- */
const SectionTitle = ({ eyebrow, children, center = true }) => (
  <div className={`mb-10 ${center ? 'text-center' : ''}`}>
    {eyebrow && (
      <p className={`eyebrow-lux mb-3 ${center ? 'justify-center' : ''}`} style={center ? { display: 'flex' } : undefined}>
        {eyebrow}
      </p>
    )}
    <h2 className="font-heading text-[30px] md:text-[40px] leading-[1.12] font-bold text-[#0b2545] tracking-tight">
      {children}
    </h2>
    <span
      className={`block h-[2px] w-16 mt-5 rounded-full ${center ? 'mx-auto' : ''}`}
      style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }}
    />
  </div>
);

/* ---------- Hero ---------- */
const Hero = () => (
  <section className="relative overflow-hidden navy-panel">
    {/* subtle grid texture */}
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.05]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }}
    />
    <div className="relative max-w-[1240px] mx-auto px-4 lg:px-6 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 items-center gap-12">
      <div className="animate-[fadeInUp_.7s_ease-out]">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-[#e8c887]/25 text-[13px] font-medium text-[#f0dcae] mb-6">
          <BadgeCheck className="w-4 h-4" style={{ color: '#5ed4bc' }} />
          Trusted by 10,000+ home owners across India
        </span>
        <h1 className="font-heading text-[38px] md:text-[56px] leading-[1.06] font-bold tracking-tight text-white">
          Home Loans made <span style={{ color: '#5ed4bc' }}>simple</span>.
          <br />From <span className="text-gold-grad">7%*</span> across 20+ banks.
        </h1>
        <p className="mt-6 text-white/75 text-[16px] md:text-[17px] leading-relaxed max-w-[540px]">
          Compare offers from India&apos;s leading banks, get instant eligibility, and receive
          doorstep assistance until disbursal. <b className="text-white">No upper limit</b> on loan amount, tenure up to <b className="text-white">30 years</b>,
          and <b className="text-white">upto 100% funding</b>.
        </p>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link to="/home-loan" className="btn-lux btn-gold">
            Apply for Home Loan <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/emi-calculator"
            className="btn-ghost-lux border border-white/30 text-white hover:bg-white/10"
          >
            Calculate EMI
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4 max-w-[520px]">
          {[
            { v: '7%*', l: 'Starting ROI' },
            { v: '30 yrs', l: 'Max Tenure' },
            { v: '100%', l: 'Funding' },
          ].map((s, i) => (
            <div
              key={i}
              className="rounded-xl bg-white/[0.07] backdrop-blur border border-white/10 p-3.5 text-center hover:border-[#e8c887]/40 transition-colors"
            >
              <p className="font-heading font-bold text-[19px] md:text-[22px] text-gold-grad">{s.v}</p>
              <p className="text-[12px] text-white/60 mt-0.5">{s.l}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center md:justify-end">
        <img
          src={heroImage}
          alt="Achieve financial independence with Finirio home loans"
          className="w-full max-w-[520px] drop-shadow-2xl animate-[floatY_5s_ease-in-out_infinite]"
          loading="eager"
        />
      </div>
    </div>
  </section>
);

/* ---------- Stats bar ---------- */
const STATS = [
  { icon: Users, l: 'Happy Customers', end: 10000, suffix: '+' },
  { icon: Building2, l: 'Banking Partners', end: 20, suffix: '+' },
  { icon: IndianRupee, l: 'Loans Disbursed', end: 5000, prefix: '₹', suffix: ' Cr+' },
  { icon: ShieldCheck, l: 'Customer Rating', end: 4.8, decimals: 1, suffix: '/5' },
];

const StatsBar = () => (
  <section className="bg-white border-b border-[#0b2545]/8 relative">
    <div className="hairline-gold" />
    <div className="max-w-[1240px] mx-auto px-4 lg:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 90}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl grid place-items-center flex-none border border-[#1e73be]/15" style={{ background: 'linear-gradient(160deg,#eaf3fb,#dcecff)' }}>
              <s.icon className="w-5 h-5" style={{ color: '#1e73be' }} />
            </div>
            <div>
              <p className="font-heading font-bold text-[22px] text-[#0b2545] leading-none">
                <CountUp end={s.end} prefix={s.prefix || ''} suffix={s.suffix || ''} decimals={s.decimals || 0} />
              </p>
              <p className="text-[13px] text-slate-500 mt-1.5">{s.l}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- Services ---------- */
const ServicesBanner = () => (
  <section aria-labelledby="services-heading" className="py-20" style={{ backgroundColor: '#FBF9F4' }}>
    <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
      <div className="mb-12 text-center">
        <p className="eyebrow-lux mb-3" style={{ display: 'inline-flex' }}>What we do</p>
        <h2 id="services-heading" className="font-heading text-[30px] md:text-[40px] font-bold text-[#0b2545] tracking-tight">
          Our Services
        </h2>
        <span className="block h-[2px] w-16 mt-5 rounded-full mx-auto" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
        {services.map((s, idx) => {
          const Icon = SERVICE_ICON_MAP[s.id];
          const inner = (
            <div className={`card-lux flex flex-col items-center text-center px-4 py-8 md:py-10 h-full ${s.comingSoon ? 'opacity-70' : ''}`}>
              <div className="relative w-20 h-20 md:w-24 md:h-24 grid place-items-center rounded-full border border-[#1e73be]/12" style={{ background: 'linear-gradient(160deg,#eaf3fb,#f5faff)' }}>
                {Icon && <Icon className="w-10 h-10 md:w-11 md:h-11" style={{ color: s.comingSoon ? '#94a3b8' : '#1e73be' }} />}
                {s.comingSoon && (
                  <span className="absolute -bottom-2 bg-slate-400 text-white text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap">
                    Coming Soon
                  </span>
                )}
              </div>
              <h3 className={`mt-5 font-heading text-[18px] md:text-[20px] font-bold transition-colors ${s.comingSoon ? 'text-slate-400' : 'text-[#0b2545] group-hover:text-[#1e73be]'}`}>
                {s.title}
              </h3>
            </div>
          );
          return (
            <Reveal key={s.id} delay={idx * 100}>
              {s.comingSoon ? (
                <div className="cursor-default h-full" aria-label={s.title}>{inner}</div>
              ) : (
                <Link to={s.href} className="group block h-full" aria-label={s.title}>{inner}</Link>
              )}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- Home Loan Feature Block ---------- */
const HomeLoanFeature = () => (
  <section className="bg-white py-20" aria-labelledby="home-loan-heading">
    <div className="max-w-[1240px] mx-auto px-4 lg:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <p className="eyebrow-lux mb-3">Featured Product</p>
        <h2 id="home-loan-heading" className="font-heading text-[30px] md:text-[40px] font-bold text-[#0b2545] tracking-tight leading-[1.12]">
          Own your dream home with a <span style={{ color: '#1e73be' }}>Finirio Home Loan</span>
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed">
          Whether you&apos;re buying your first home, upgrading, or transferring an existing loan for a better rate — Finirio
          helps you compare offers from 20+ leading banks and choose the best fit for your income, tenure and dream property.
        </p>

        <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { i: Percent, t: 'Interest from 7%*' },
            { i: IndianRupee, t: 'No upper limit on loan amount' },
            { i: Clock3, t: 'Tenure up to 30 years' },
            { i: ShieldCheck, t: 'Upto 100% funding' },
            { i: BadgeCheck, t: 'Age eligibility up to 70 years' },
            { i: Users, t: 'Doorstep documentation' },
          ].map((f, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px] text-slate-800">
              <span
                className="w-8 h-8 rounded-lg grid place-items-center flex-none border border-[#2ea690]/15"
                style={{ background: 'linear-gradient(160deg,#e7f6f2,#f2fbf8)' }}
              >
                <f.i className="w-4 h-4" style={{ color: '#2ea690' }} />
              </span>
              <span className="mt-1">{f.t}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/home-loan" className="btn-lux">
            Apply Now <ChevronRight className="w-4 h-4" />
          </Link>
          <Link
            to="/eligibility-calculator"
            className="btn-ghost-lux border border-[#0b2545]/20 text-[#0b2545] hover:bg-[#0b2545]/5"
          >
            Check Eligibility
          </Link>
          <Link
            to="/why-finirio"
            className="btn-ghost-lux text-[#1e73be] hover:bg-[#1e73be]/5"
          >
            Why Finirio? <ChevronRight className="w-4 h-4" />
          </Link>
          <a
            href="tel:+917972467340"
            className="btn-ghost-lux text-[#1e73be] hover:bg-[#1e73be]/5"
          >
            <Phone className="w-4 h-4" /> Talk to Advisor
          </a>
        </div>
      </Reveal>

      <Reveal delay={140}>
        <div className="relative">
          <div className="absolute -inset-3 rounded-3xl border border-[#c9a24b]/30 hidden md:block" aria-hidden="true" />
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_28px_60px_-24px_rgba(11,37,69,.4)]"
            style={{ aspectRatio: '4/3' }}
          >
            <img
              src="/images/family-home.jpg"
              alt="Happy Indian family in front of their new home purchased with Finirio home loan"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white shadow-[0_18px_40px_-16px_rgba(11,37,69,.35)] rounded-xl border border-[#c9a24b]/25 p-4 hidden md:flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full grid place-items-center"
              style={{ backgroundColor: '#e7f6f2' }}
            >
              <BadgeCheck className="w-5 h-5" style={{ color: '#2ea690' }} />
            </div>
            <div>
              <p className="font-heading font-bold text-[#0b2545] leading-none">7-day Disbursal</p>
              <p className="text-[12px] text-slate-600 mt-1">Fastest in the industry</p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- Banks ---------- */
const Banks = () => {
  const doubled = [...banks, ...banks];
  return (
    <section style={{ backgroundColor: '#FBF9F4' }} aria-labelledby="banks-heading">
      <div className="max-w-[1240px] mx-auto px-0 lg:px-6 py-16">
        <div className="flex flex-col lg:flex-row items-stretch gap-0 lg:gap-6">
          <div className="lg:w-[340px] flex-none grid place-items-center py-10 lg:py-0 navy-panel lg:rounded-2xl">
            <h2
              id="banks-heading"
              className="font-heading text-white text-center text-[24px] md:text-[27px] font-bold px-6"
            >
              Banks In Our Network
              <span className="block text-[15px] font-normal mt-2 text-[#e8c887]">(40+ Banks &amp; NBFCs)</span>
            </h2>
          </div>
          <div className="flex-1 overflow-hidden relative py-6">
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10 bg-gradient-to-r from-[#FBF9F4] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10 bg-gradient-to-l from-[#FBF9F4] to-transparent" />
            <div className="flex gap-8 animate-[scrollX_28s_linear_infinite] w-max hover:[animation-play-state:paused]">
              {doubled.map((b, i) => (
                <div key={i} className="flex-none w-[160px] h-[80px] flex items-center justify-center px-4 bg-white rounded-xl border border-[#0b2545]/8 shadow-sm hover:shadow-md hover:border-[#c9a24b]/35 transition-all">
                  <img
                    src={b.img}
                    alt={`${b.name} - Finirio banking partner`}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- How it works ---------- */
const HowItWorks = () => (
  <section className="py-20" style={{ backgroundColor: '#f2f0e9' }} aria-labelledby="how-heading">
    <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
      <SectionTitle eyebrow="Process">
        <span id="how-heading">How it works</span>
      </SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        {howItWorks.map((h, idx) => (
          <Reveal key={h.id} delay={idx * 110}>
            <article className="card-lux relative p-8 text-center h-full">
              <span
                className="absolute -top-3 left-6 text-[11px] font-bold tracking-[.12em] px-3 py-1 rounded-full text-[#16233a]"
                style={{ background: 'linear-gradient(135deg,#e8c887,#c9a24b)' }}
              >
                STEP {h.id}
              </span>
              <div className="w-24 h-24 mx-auto mb-5 grid place-items-center rounded-full border border-[#1e73be]/12" style={{ background: 'linear-gradient(160deg,#eaf3fb,#f5faff)' }}>
                {(() => { const Icon = HOW_ICON_MAP[h.id]; return Icon ? <Icon className="w-11 h-11" style={{ color: '#1e73be' }} /> : null; })()}
              </div>
              <h3 className="font-heading text-[18px] font-bold text-[#0b2545] mb-2">{h.title}</h3>
              <p className="text-[14px] text-slate-600 leading-relaxed">{h.desc}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Testimonials ---------- */
const Testimonials = () => (
  <section className="pb-24 pt-10" style={{ backgroundColor: '#f2f0e9' }} aria-labelledby="testimonials-heading">
    <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
      <SectionTitle eyebrow="Success Stories">
        <span id="testimonials-heading">Testimonials</span>
      </SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {testimonials.map((t, idx) => (
          <Reveal key={t.id} delay={(idx % 3) * 100}>
            <article className="card-lux p-6 text-left flex gap-4 items-start h-full">
              <img
                src={t.img}
                alt={`${t.name} testimonial photo`}
                className="w-[72px] h-[72px] rounded-full object-cover flex-none border-2 border-[#e8c887]/60 shadow"
                loading="lazy"
              />
              <div className="flex-1">
                <p className="text-slate-700 text-[14px] leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-1 mt-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className="w-4 h-4"
                      fill={n <= Math.round(t.rating) ? '#c9a24b' : 'none'}
                      stroke={n <= Math.round(t.rating) ? '#c9a24b' : '#d1d5db'}
                    />
                  ))}
                </div>
                <h4 className="mt-2 font-heading font-bold text-[#0b2545]">
                  {t.name}
                </h4>
                <p className="text-slate-500 text-[13px]">{t.role}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const faqs = [
  {
    q: 'What is the minimum interest rate for a Home Loan at Finirio?',
    a: 'Home loan interest rates start at 7%* per annum and vary based on your credit score, loan amount, tenure, employment type and the lender. Finirio helps you compare offers from 20+ banks to secure the best rate.',
  },
  {
    q: 'What is the maximum home loan amount I can get?',
    a: 'There is no upper cap on the loan amount at Finirio. Your eligible amount depends on your income, credit profile and the property value — with upto 100% funding available on select cases.',
  },
  {
    q: 'What is the maximum tenure for a home loan?',
    a: 'Home loans through Finirio come with flexible tenure options of up to 30 years, allowing you to choose comfortable EMIs.',
  },
  {
    q: 'Who is eligible for a home loan?',
    a: 'Salaried employees, self-employed professionals and business owners aged between 21 and 70 years with a stable income and a good credit score are eligible. Minimum monthly income of ₹25,000 is recommended.',
  },
  {
    q: 'How long does it take to disburse a home loan?',
    a: 'Once all documents are submitted, home loans are typically sanctioned in 3–5 working days and disbursed within 7 days.',
  },
  {
    q: 'Does Finirio charge any fee for its services?',
    a: 'No. Finirio\u2019s service is completely free for customers — banks pay Finirio a marketing/business fee for the monthly loan volume it brings, so customers are never charged for advisory or processing support.',
  },
  {
    q: 'Why do Finirio customers get lower interest rates than walk-in bank customers?',
    a: 'Finirio brings partner banks a large volume of loan files every month — like 5–6 friends approaching a Branch Manager together to negotiate a better deal. In exchange for this bulk business, banks offer Finirio customers a lower rate of interest than an individual walk-in customer typically gets for the same profile.',
  },
  {
    q: 'How much can I save on loan charges through Finirio?',
    a: 'On a ₹1 Crore file, direct-to-bank charges are typically around ₹60,000. Through Finirio you pay only about ₹30,000–₹45,000 — Finirio pays the remaining difference to the bank on your behalf, a saving of roughly 25–50%.',
  },
  {
    q: 'Can I transfer my existing home loan to another bank?',
    a: 'Yes, Finirio offers a balance transfer facility that lets you switch your existing home loan to a bank offering better interest rates and terms — often resulting in significant savings.',
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return (
    <section className="bg-white py-20" aria-labelledby="faq-heading">
      <SEO
        title="Finirio | Home Loans in India from 7%* — Empowering Your Financial Future"
        description="Get the best Home Loan in India at rates starting 7%* with Finirio. Compare offers from 20+ banks, no upper limit on amount, up to 100% funding, age up to 70 years, tenure up to 30 years."
        path="/"
        keywords="home loan india, best home loan rate 7%, home loan emi calculator, 100% home loan funding, home loan up to 70 years, home loan eligibility, apply home loan online, Finirio"
        jsonLd={jsonLd}
        jsonLdId="faq-jsonld"
      />
      <div className="max-w-[900px] mx-auto px-4 lg:px-6">
        <SectionTitle eyebrow="FAQs">
          <span id="faq-heading">Frequently Asked Questions</span>
        </SectionTitle>
        <div className="mt-4 space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className={`border rounded-xl transition-all duration-300 ${
                  isOpen
                    ? 'border-[#c9a24b]/45 bg-[#fdfbf5] shadow-[0_12px_28px_-16px_rgba(11,37,69,.18)]'
                    : 'border-slate-200 bg-white hover:border-[#1e73be]/30'
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between text-left px-5 py-4"
                  aria-expanded={isOpen}
                >
                  <span className="font-heading font-semibold text-[#0b2545] text-[15px] md:text-[16.5px] pr-4">
                    {f.q}
                  </span>
                  <span
                    className="w-8 h-8 rounded-full grid place-items-center flex-none transition-colors"
                    style={
                      isOpen
                        ? { background: 'linear-gradient(135deg,#e8c887,#c9a24b)', color: '#16233a' }
                        : { backgroundColor: '#eaf3fb', color: '#1e73be' }
                    }
                  >
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-slate-700 text-[15px] leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- CTA Band ---------- */
const CTABand = () => (
  <section className="relative overflow-hidden py-16 navy-panel">
    <div className="hairline-gold absolute top-0 left-0 right-0" />
    <div className="relative max-w-[1240px] mx-auto px-4 lg:px-6 flex flex-col md:flex-row items-center justify-between gap-8 text-white text-center md:text-left">
      <Reveal>
        <h3 className="font-heading text-[26px] md:text-[32px] font-bold leading-tight">
          Ready to take the next step towards your dream home?
        </h3>
        <p className="text-white/70 mt-3 max-w-[640px]">
          Get instant eligibility, compare offers from 20+ banks and speak to a dedicated advisor — all under one roof.
        </p>
      </Reveal>
      <Reveal delay={120}>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/home-loan" className="btn-lux btn-gold">
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/emi-calculator"
            className="btn-ghost-lux border border-white/40 text-white hover:bg-white/10"
          >
            EMI Calculator
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- Home ---------- */
const Home = () => (
  <main>
    <Hero />
    <StatsBar />
    <ServicesBanner />
    <HomeLoanFeature />
    <Banks />
    <HowItWorks />
    <section className="bg-white py-20" aria-labelledby="emi-heading">
      <div className="max-w-[1240px] mx-auto px-4 lg:px-6">
        <SectionTitle eyebrow="Plan Ahead">
          <span id="emi-heading">Home Loan EMI Calculator</span>
        </SectionTitle>
        <EMICalculator />
      </div>
    </section>
    <Testimonials />
    <FAQ />
    <CTABand />
  </main>
);

export default Home;
